import type {Asset} from '@hconnect/common'
import {dataTestId} from '@hconnect/uikit'
import {CardTitle, LoadingButton} from '@hconnect/uikit/src/lib2'
import {Close} from '@mui/icons-material'
import {Box, Button, Dialog, DialogContent, Stack, Theme, useMediaQuery} from '@mui/material'
import React, {FC} from 'react'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {GrindingCircuit, GrindingCircuitPayload} from '../../types'

import {GrindingCircuitsFormFields} from './GrindingCircuitsFormFields'

type GrindingCircuitsFormDialogProps = {
  onClose: () => void
  onSubmit: (data: GrindingCircuitPayload) => void
  isLoading: boolean
  assets?: Asset[]
  defaultValues?: GrindingCircuit
  isEdit?: boolean // todo implement edition HCP-89878
}

export const GrindingCircuitsFormDialog: FC<GrindingCircuitsFormDialogProps> = ({
  isEdit,
  onClose,
  isLoading,
  onSubmit,
  assets = [],
  defaultValues
}) => {
  const {t} = useTranslation()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const {handleSubmit, control, formState, watch, setValue} = useForm<GrindingCircuitPayload>({
    mode: 'all',
    shouldFocusError: false,
    defaultValues: defaultValues
      ? {name: defaultValues.name, assetIds: defaultValues.assetIds}
      : {name: '', assetIds: []}
  })

  const submit = handleSubmit((data) => {
    if (!formState.isValid) return
    onSubmit(data)
  })

  const assetIdsCount = (watch('assetIds') || []).length

  return (
    <Dialog
      open
      fullWidth
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return
        onClose()
      }}
    >
      <DialogContent sx={{display: 'flex', flexDirection: 'column', overflowY: 'auto'}}>
        <CardTitle>
          {t(
            `plantSetup.grindingCircuit.label.${
              isEdit ? 'editGrindingCircuit' : 'newGrindingCircuit'
            }`
          )}
        </CardTitle>

        <Stack content="form" spacing={3} overflow="auto" {...dataTestId('grinding_circuit_form')}>
          <GrindingCircuitsFormFields assets={assets} control={control} />

          <Box
            sx={{
              display: 'flex',
              mt: 2,
              justifyContent: assetIdsCount && !isMobile ? 'space-between' : 'flex-end'
            }}
          >
            {!!assetIdsCount && !isMobile && (
              <Button
                variant="text"
                endIcon={<Close />}
                onClick={() => setValue('assetIds', [], {shouldValidate: true, shouldTouch: true})}
                {...dataTestId('clear-finish-mills')}
              >
                {t('plantSetup.grindingCircuit.action.clearSelectCementMill', {
                  count: assetIdsCount
                })}
              </Button>
            )}

            <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1}}>
              <Button
                color="primary"
                variant="text"
                {...dataTestId('grinding_circuit_dialog_cancel_button')}
                sx={{
                  minWidth: (theme) => theme.spacing(15)
                }}
                onClick={onClose}
                startIcon={<Close />}
              >
                {t('common.cancel')}
              </Button>

              <LoadingButton
                variant="contained"
                {...dataTestId('grinding_circuit_dialog_main_action_button')}
                disabled={!formState.isValid || isLoading}
                onClick={submit}
                color="primary"
                isLoading={isLoading}
                sx={{minWidth: (theme) => theme.spacing(15)}}
              >
                {t('common.save')}
              </LoadingButton>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
